"use client";

import React, { useState, useEffect, CSSProperties } from "react";
import { Env } from "@/utils/common/env";
import { PUBLIC_ENV_VARS } from "@/utils/common/environment";
import Image from "next/image";

interface EnvironmentNotificationBarProps {
  onVisibilityChange?: (visible: boolean) => void;
}

export function EnvironmentNotificationBar({ onVisibilityChange }: EnvironmentNotificationBarProps) {
  const [deployDateTime, setDeployDateTime] = useState<string>("");
  const [isVisible, setIsVisible] = useState<boolean>(true);

  useEffect(() => {
    // Get the build timestamp or deployment time from environment variables
    const buildTime = PUBLIC_ENV_VARS.BUILD_TIME || new Date().toISOString();
    const deployTime = new Date(buildTime).toLocaleString();
    setDeployDateTime(deployTime);

    // Check if the bar was previously closed in this session
    const isBarClosed = sessionStorage.getItem('environmentBarClosed') === 'true';
    const visible = !isBarClosed;
    setIsVisible(visible);
    onVisibilityChange?.(visible && !Env.IsProduction());
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('environmentBarClosed', 'true');
    onVisibilityChange?.(false);
  };

  if (Env.IsProduction() || !isVisible) {
    return null; // No bar for production or if closed
  }

  const getBarConfig = () => {
    if (Env.IsStaging()) {
      return {
        bgColor: "bg-blue-900",
        textColor: "text-white",
        message: `STAGING ENVIRONMENT ${deployDateTime}`,
      };
    }

    if (Env.IsPreview()) {
      return {
        bgColor: "bg-purple-800",
        textColor: "text-white",
        message: `PREVIEW ENVIRONMENT ${deployDateTime}, CAN CHANGE ANY MOMENT`,
      };
    }

    if (Env.IsDevelopment()) {
      return {
        bgColor: "bg-red-600",
        textColor: "text-white",
        message: "DEVELOPMENT ENVIRONMENT",
      };
    }

    return null;
  };

  const config = getBarConfig();
  if (!config) return null;

  return (
    <div
      className={`
        fixed top-0 left-0 right-0 z-50 w-full px-4 py-2 text-center text-sm font-semibold
        ${config.bgColor} ${config.textColor}
      `}
    >
      <div className="relative">
        {config.message}
        <button
          onClick={handleClose}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 
            hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
          aria-label="Close notification"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  );
}

interface EnvironmentLogoProps {
  src: string;
  alt: string;
  className?: string;
}

export function EnvironmentLogo({ src, alt, className = "h-8" }: EnvironmentLogoProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const getLogoConfig = () => {
    if (Env.IsProduction()) {
      return {
        text: null,
        tooltip: "Production environment. Real data. Real customers.",
        filter: "",
        style: {} as CSSProperties,
      };
    }

    if (Env.IsStaging()) {
      return {
        text: null,
        tooltip: "Staging environment, test data only",
        filter: "grayscale(100%)",
        style: {
          filter: "grayscale(100%) brightness(0.6)",
        } as CSSProperties,
      };
    }

    if (Env.IsPreview()) {
      return {
        text: "Preview",
        tooltip: "Preview environment. Can change at any time. Test data only.",
        filter: "grayscale(100%) brightness(0.8)",
        style: {
          filter: "grayscale(100%) brightness(0.8) opacity(0.7)",
        } as CSSProperties,
      };
    }

    if (Env.IsDevelopment()) {
      return {
        text: "Development",
        tooltip: "Development environment. Usually running locally only. Test data only.",
        filter: "grayscale(100%) brightness(0.9)",
        style: {
          filter: "grayscale(100%) brightness(0.9) opacity(0.5)",
        } as CSSProperties,
      };
    }

    return {
      text: null,
      tooltip: "",
      filter: "",
      style: {} as CSSProperties,
    };
  };

  const config = getLogoConfig();

  return (
    <div className="relative inline-block">
      <div
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Logo Image */}
        <div className="relative">
          <Image
            src={src}
            alt={alt}
            width={120}
            height={32}
            className={className}
            style={config.style}
          />
        </div>
        
        {/* Environment Text */}
        {config.text && (
          <div className="text-center text-xs text-gray-600 mt-1 font-medium">
            {config.text}
          </div>
        )}

        {/* Tooltip */}
        {showTooltip && config.tooltip && (
          <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 z-50">
            <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
              {config.tooltip}
              <div className="absolute right-full top-1/2 transform -translate-y-1/2 
                border-4 border-transparent border-r-gray-800"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}